import {
  Box,
  FormControl,
  FormControlLabel,
  Grid2,
  Radio,
  RadioGroup,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateHandleUnsaveNavigation } from "../../../../utilities/handleUnsaveNavigation";
import dayjs from "dayjs";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../../hooks/useNavigationGuard";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import SearchableSelect from "../../../../components/SearchableSelect";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import StyledGridComp from "../../../../components/StyledGridComp";
import Loader from "../../../../components/Loader";
import {
  acknowledgeDrugOrderService,
  getAcknowledgeOrderOnLoadDataService,
  getDrugKitDataService,
} from "../../../../services/drugOrderManagementService";
import { notifySuccess, notifyWarning } from "../../../../utilities/helper";
import customParseFormat from "dayjs/plugin/customParseFormat";
import UncontrolledSearchSelect from "../../../../components/UncontrolledSearchSelect";
import ConfirmAcknowledge from "./ConfirmAcknowledge";

//  this is for formatting the date for the validation in the received date field
dayjs.extend(customParseFormat);

const AcknowledgeDrugOrder = () => {
  const id = useLocation()?.state?.id;
  const roleId = localStorage?.getItem("roleId");
  const access_token = localStorage?.getItem("access_token");

  const [load, setLoad] = useState(false);
  const [orderNumber, setOrderNumner] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [drugData, setDrugData] = useState(null);
  const [orderDate, setOrderDate] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [rowData, setRowData] = useState([]);
  const [rowLength, setRowLength] = useState(0);

  const [showConfirmPage, setShowConfirmPage] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const navigate = useNavigate();

  const createAcknowledgeOrderSchema = yup.object().shape({
    temperatureExursion: yup.string().when("$showAlarmQuestion", {
      is: true,
      then: (schema) =>
        schema
          .oneOf(["yes", "no"], "Temperature Excursion is required")
          .required("Temperature Excursion is required"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    receivedDate: yup
      .mixed()
      .test("is-valid-date", "Received date is required", (value) =>
        dayjs(value).isValid()
      )
      .nullable(),
    applyDrugKitStatus: yup
      .string()
      .required("Apply Drug Status To All Kits is required"),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createAcknowledgeOrderSchema),
    context: { showAlarmQuestion: Boolean(drugData?.showAlarmQuestion) },
    defaultValues: {
      applyDrugKitStatus: "",
      receivedDate: "",
      temperatureExursion: "",
    },
  });

  useNavigationGuard(isDirty);

  const selectedStatus = watch("applyDrugKitStatus");

  // This is for applying the selected status to all the rows in the grid
  useEffect(() => {
    if (selectedStatus && rowData.length > 0) {
      const updatedRows = rowData.map((row) => {
        return { ...row, selectedStatusVal: selectedStatus };
      });
      setRowData(updatedRows);
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (access_token && id) {
      getOnLoadData();
    }
  }, [access_token, id]);

  useEffect(() => {
    if (access_token && id) {
      getDataForDrugKitGrid(pageSize, page);
    }
  }, [id]);

  const columns = [
    {
      field: "kitNumber",
      headerName: "Drug Kit Number",
      width: 460,
      renderHeader: () => (
        <div className="grid-heading">{"Drug Kit Number"}</div>
      ),
      renderCell: (params) => {
        const kitNumber = params.row.kitNumber;

        return <span className="data-font">{kitNumber}</span>;
      },
    },
    {
      field: "currentStatus",
      headerName: "Current Status",
      width: 460,
      renderHeader: () => (
        <div className="grid-heading">{"Current Status"}</div>
      ),
      renderCell: (params) => {
        const currentStatus = params.row.currentStatus;

        return <span className="data-font">{currentStatus}</span>;
      },
    },
    {
      field: "selectedStatus",
      headerName: "Selected Status",
      filterable: false,
      width: 460,
      renderHeader: () => (
        <div className="grid-heading">{"Selected Status"}</div>
      ),
      renderCell: (params) => {
        const currentStatus = params.row.currentStatus;

        return (
          <UncontrolledSearchSelect
            data={statusOptions}
            optionLabel="key"
            optionKey="value"
            noDefault={true}
            isReadOnly={false}
            placeholder="Select Status"
            value={params.row.selectedStatusVal || "none"}
            onChange={(val) => {
              const updatedRows = rowData.map((row) => {
                if (row.id === params.row.id) {
                  return { ...row, selectedStatusVal: val };
                }
                return row;
              });
              setRowData(updatedRows);
            }}
          />
        );
      },
    },
  ];

  const getOnLoadData = async () => {
    try {
      setLoad(true);
      const res = await getAcknowledgeOrderOnLoadDataService(access_token, id);
      if (res?.data?.message == "Data Extracted Successfully!") {
        setLoad(false);
        setOrderNumner(res?.data?.result?.model?.orderNumber);
        setStatusOptions(
          res?.data?.result?.drugStatuses?.map((item) =>
            item?.key === ""
              ? {
                  ...item,
                  key: "None",
                  value: "00000000-0000-0000-0000-000000000000",
                }
              : item
          )
        );

        setOrderDate(res?.data?.result?.model?.orderDate);
        setDrugData(res?.data?.result?.model);
      } else if (
        res?.data?.message ==
        "Order has already been acknowledged Or Cancelled!"
      ) {
        notifySuccess("Order has already been acknowledged Or Cancelled!");
        navigate("/drug-shipment-management");
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err);
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const getDataForDrugKitGrid = async (pageSize, page) => {
    setLoad(true);
    try {
      const res = await getDrugKitDataService(
        access_token,
        id,
        (page = page + 1),
        (pageSize = 100)
      );
      if (
        res?.data?.result?.status == 200 &&
        res?.data?.result?.result?.length > 0
      ) {
        setLoad(false);
        setRowData(
          res.data.result?.result?.map((item, idx) => ({
            ...item,
            id: item.drugId || idx, // fallback to idx if no id present
          }))
        );
        setRowLength(res?.data?.result?.totalCount);
      }
    } catch (err) {
      setLoad(false);
      console.log("Err", err);
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const submitAcknowledgedDrug = async (data) => {
    try {
      setLoad(true);
      const newRowData = rowData.map((item) => ({
        drugId: item.drugId,
        drugStatusId: item.selectedStatusVal,
      }));
      const payload = {
        id: id,
        hasAlarm: data?.temperatureExursion === "yes" ? true : false,
        orderDateTimeOffset: drugData?.orderDateTimeOffset,
        receivedDate: dayjs(data?.receivedDate, "DD-MMM-YYYY").toISOString(),
        destinationDepotId: drugData?.destinationDepotId,
        destinationTimeZone: drugData?.destinationTimeZone,
        applyDrugStatus: data?.applyDrugKitStatus,
        destinationSiteId: drugData?.destinationSiteId,
        drugUpdateJson: JSON.stringify(
          transFormRowDataForPayload(rowData, statusOptions)
        ),
      };
      const res = await acknowledgeDrugOrderService(
        access_token,
        payload,
        roleId
      );
      if (
        res?.data?.message == "Acknowledged successfully!" ||
        res?.data?.status === 200
      ) {
        setLoad(false);
        notifySuccess("Order Acknowledged Successfully!");
        // navigate("/drug-shipment-management");
        setShowConfirmPage(true);
        setConfirmData(res?.data?.result);
      } else if (
        res?.data?.message === "One or more Kits require a status update."
      ) {
        notifyWarning(res?.data?.message);
        setLoad(false);
      }
    } catch (err) {
      console.log("Err", err);
      setShowConfirmPage(false);
      setConfirmData(null);
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
      setLoad(false);
    }
  };

  // This function will make changes to the grid data such that it will create the statuses array with the selected status from the grid Select along with the object such that the key will be the drugId
  function transFormRowDataForPayload(dataArray, statusOptions) {
    return dataArray.reduce((acc, item) => {
      const { selectedStatusVal, ...rest } = item;

      // Find matching status name
      const matchedStatus = statusOptions.find(
        (status) => status.value === selectedStatusVal
      );

      acc[item.drugId] = {
        drugId: item.drugId,
        siteName: item.siteName || "", // fallback if not in original
        kitNumber: item.kitNumber,
        currentStatus: item.currentStatus,
        currentStatusId: item.currentStatusId,
        selectedStatus: selectedStatusVal,
        statuses: [
          {
            id: selectedStatusVal,
            name: matchedStatus ? matchedStatus.key : "",
          },
        ],
      };

      return acc;
    }, {});
  }

  return (
    <>
      {showConfirmPage ? (
        <ConfirmAcknowledge
          setShowConfirm={setShowConfirmPage}
          data={confirmData}
        />
      ) : (
        <>
          {load ? (
            <Loader />
          ) : (
            <div className="content-body">
              <p className="bread-crumbs-link-container">
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(e, isDirty, "", navigate)
                  }
                >
                  Home
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "drug-shipment-management",
                      navigate
                    )
                  }
                >
                  Drug Order Management
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  to="/acknowledge-drug-order"
                >
                  Acknowledge Order
                </button>
                -{" "}
                <b style={{ color: "gray" }}>
                  {localStorage?.getItem("studyName")}
                </b>
              </p>

              <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", margin: "0 auto" }}
              >
                <Grid2 item md={4}>
                  <p className="page-heading">Acknowledge Order</p>
                </Grid2>
              </Grid2>

              <div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    margin: "20px auto",
                    textAlign: "center",
                  }}
                >
                  Order Number #{orderNumber}
                </div>
                <Box
                  component="form"
                  autoComplete="off"
                  sx={{
                    height: "auto",
                  }}
                  onSubmit={handleSubmit(submitAcknowledgedDrug)}
                >
                  <Grid2
                    container
                    alignItems="center"
                    justifyContent={"space-between"}
                    sx={{ width: "50%", margin: "0 auto" }}
                    gap={1}
                  >
                    {drugData?.showAlarmQuestion && (
                      <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                        <div>
                          <p className="labelStyles">
                            Was there a temperature excursion in this Order?
                            <span>*</span>
                          </p>
                        </div>
                        <div>
                          <Controller
                            name="temperatureExursion"
                            control={control}
                            render={({ field }) => (
                              <FormControl>
                                <RadioGroup {...field}>
                                  <FormControlLabel
                                    value="yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "14px",
                                      },
                                    }}
                                  />
                                  <FormControlLabel
                                    value="no"
                                    control={<Radio size="small" />}
                                    label="No"
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "14px",
                                      },
                                    }}
                                  />
                                </RadioGroup>
                              </FormControl>
                            )}
                          />
                        </div>
                        {errors.temperatureExursion && (
                          <div className="error-container">
                            {errors.temperatureExursion.message}
                          </div>
                        )}
                      </Grid2>
                    )}
                    <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                      <div>
                        <p className="labelStyles">
                          Recieved Date
                          <span>*</span>
                        </p>
                      </div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          name="receivedDate"
                          control={control}
                          rules={{ required: "Received date is required" }} // Optional validation rule
                          render={({ field }) => (
                            <DatePicker
                              sx={{ width: "100%" }}
                              value={field.value ? dayjs(field.value) : null}
                              onChange={(date) =>
                                field.onChange(
                                  date
                                    ? dayjs(date).format("DD-MMM-YYYY")
                                    : null
                                )
                              }
                              format="DD-MMM-YYYY"
                              // shouldDisableDate={(date) =>
                              //   orderDate
                              //     ? date.isBefore(
                              //         dayjs(orderDate, "DD-MMM-YYYY"),
                              //         "day"
                              //       )
                              //     : false
                              // }
                              shouldDisableDate={(date) => {
                                if (!orderDate) return false;

                                const minDate = dayjs(orderDate, "DD-MMM-YYYY");
                                const maxDate = dayjs(); // today

                                // disable if before orderDate OR after today
                                return (
                                  date.isBefore(minDate, "day") ||
                                  date.isAfter(maxDate, "day")
                                );
                              }}
                              slotProps={{
                                textField: {
                                  placeholder: "DD-MMM-YYYY",
                                  sx: {
                                    "& .MuiInputBase-root": { height: "40px" },
                                  },
                                  size: "small",
                                  fullWidth: true,
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {errors.receivedDate && (
                        <div className="error-container">
                          {errors.receivedDate.message}
                        </div>
                      )}
                    </Grid2>
                    <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                      <div>
                        <p className="labelStyles">
                          Apply Drug Status to All Kits
                          <span>*</span>
                        </p>
                        <SearchableSelect
                          control={control}
                          data={statusOptions || []}
                          noDefault={true}
                          name={"applyDrugKitStatus"}
                          optionKey="value"
                          optionLabel="key"
                        />
                      </div>
                      {errors.applyDrugKitStatus && (
                        <div className="error-container">
                          {errors.applyDrugKitStatus.message}
                        </div>
                      )}
                    </Grid2>
                  </Grid2>

                  <div
                    style={{
                      margin: "40px 0px",
                      maxHeight: "500px",
                      overflowY: "auto",
                    }}
                  >
                    <StyledGridComp
                      rows={rowData}
                      columns={columns}
                      page={page}
                      setPage={setPage}
                      pageSize={pageSize}
                      setPageSize={setPageSize}
                      length={rowLength}
                      isPaginationClientSide={true}
                    ></StyledGridComp>
                  </div>

                  <Grid2
                    alignItems={"end"}
                    justifyContent="end"
                    container
                    gap={3}
                    sx={{ marginTop: "2%" }}
                  >
                    <Grid2 item md={6}>
                      <button
                        type="button"
                        className="create-btn-outlined"
                        onClick={(e) => {
                          e.preventDefault();
                          handleUnsaveNavigation(
                            e,
                            isDirty,
                            "drug-shipment-management",
                            navigate
                          );
                        }}
                      >
                        Cancel
                      </button>
                    </Grid2>
                    <Grid2 item md={6}>
                      <button type="submit" className="create-btn">
                        Save
                      </button>
                    </Grid2>
                  </Grid2>
                </Box>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AcknowledgeDrugOrder;
